import { useEffect, useState } from 'react';
import {
  chessBoard,
  chessTargets,
  type GamePlayer,
  type GameSnapshot,
  type MapObject,
} from '@etown/shared';
import { Modal } from '../../components/Modal';
import { useOffice } from '../office/OfficeContext';
import './objects.css';

const SEAT_LABELS: Record<string, { w: string; b: string }> = {
  chess: { w: 'Weiß', b: 'Schwarz' },
  connect4: { w: 'Rot', b: 'Gelb' },
  ttt: { w: 'X', b: 'O' },
};
const CHESS_GLYPHS: Record<string, string> = {
  wp: '♙', wn: '♘', wb: '♗', wr: '♖', wq: '♕', wk: '♔',
  bp: '♟', bn: '♞', bb: '♝', br: '♜', bq: '♛', bk: '♚',
};
const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

/**
 * Spieltisch: server-autoritativ, zwei Spieler + beliebig viele Zuschauer.
 * Nur der sitzende Spieler am Zug kann ziehen; Zuschauer sehen live zu.
 */
export function BoardGameModal({ object, onClose }: { object: MapObject; onClose: () => void }) {
  const { socket } = useOffice();
  const [snap, setSnap] = useState<GameSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null); // Schach: gewaehltes Feld

  useEffect(() => {
    let active = true;
    socket.emit('game:join', { objectId: object.id }, (res) => {
      if (active && res.ok) setSnap(res.data);
    });
    const onState = (payload: { snapshot: GameSnapshot }): void => {
      if (payload.snapshot.objectId === object.id) {
        setSnap(payload.snapshot);
        setSelected(null);
      }
    };
    socket.on('game:state', onState);
    return () => {
      active = false;
      socket.off('game:state', onState);
      socket.emit('game:leave', { objectId: object.id });
    };
  }, [socket, object.id]);

  const labels = SEAT_LABELS[snap?.game ?? 'chess'] ?? SEAT_LABELS.chess!;
  const mySeat: GamePlayer | null = snap
    ? snap.seats.w?.socketId === socket.id ? 'w' : snap.seats.b?.socketId === socket.id ? 'b' : null
    : null;
  const myTurn = mySeat !== null && snap?.status === 'playing' && snap.turn === mySeat;

  const sit = (seat: GamePlayer): void => {
    setError(null);
    socket.emit('game:sit', { objectId: object.id, seat }, (res) => {
      if (!res.ok) setError(res.error);
    });
  };
  const doMove = (move: string): void => {
    setError(null);
    socket.emit('game:move', { objectId: object.id, move }, (res) => {
      if (!res.ok) setError(res.error);
    });
  };
  const reset = (): void => {
    socket.emit('game:reset', { objectId: object.id }, () => undefined);
  };

  const title = object.props.label ?? 'Spieltisch';

  return (
    <Modal title={title} onClose={onClose}>
      {!snap ? (
        <p>Lädt…</p>
      ) : (
        <div className="game">
          <div className="game__status">
            {statusText(snap, labels)}
          </div>

          {snap.game === 'chess' && (
            <ChessBoard snap={snap} myTurn={myTurn} mySeat={mySeat} selected={selected} setSelected={setSelected} onMove={doMove} />
          )}
          {snap.game === 'connect4' && <Connect4 snap={snap} myTurn={myTurn} onMove={doMove} />}
          {snap.game === 'ttt' && <TicTacToe snap={snap} myTurn={myTurn} onMove={doMove} />}

          {error && <p className="game__error" role="alert">{error}</p>}

          <div className="game__seats">
            {(['w', 'b'] as const).map((seat) => {
              const occ = snap.seats[seat];
              const taken = occ && occ.socketId !== socket.id;
              return (
                <button
                  key={seat}
                  className={`game__seat ${mySeat === seat ? 'game__seat--mine' : ''}`}
                  disabled={!!taken}
                  onClick={() => sit(seat)}
                  title={taken ? `${occ!.displayName} sitzt hier` : `Als ${labels[seat]} spielen`}
                >
                  {labels[seat]}: {occ ? occ.displayName : 'frei'}
                </button>
              );
            })}
            <span className="game__spectators">👀 {snap.spectators}</span>
            {snap.status === 'over' && <button className="game__reset" onClick={reset}>Neues Spiel</button>}
          </div>
          {mySeat === null && <p className="game__hint">Du schaust zu — nimm oben einen Platz ein, um mitzuspielen.</p>}
        </div>
      )}
    </Modal>
  );
}

function statusText(snap: GameSnapshot, labels: { w: string; b: string }): string {
  if (snap.status === 'waiting') return 'Warten auf zwei Spieler…';
  if (snap.status === 'over') {
    if (snap.winner === 'draw') return 'Unentschieden.';
    if (snap.winner) return `${labels[snap.winner]} gewinnt! 🎉`;
    return 'Spiel beendet.';
  }
  return `Am Zug: ${labels[snap.turn]}`;
}

function ChessBoard({
  snap, myTurn, mySeat, selected, setSelected, onMove,
}: {
  snap: GameSnapshot;
  myTurn: boolean;
  mySeat: GamePlayer | null;
  selected: string | null;
  setSelected: (s: string | null) => void;
  onMove: (m: string) => void;
}) {
  const board = chessBoard(snap.board);
  const targets = selected ? chessTargets(snap.board, selected) : [];

  const clickSquare = (row: number, col: number): void => {
    if (!myTurn || !mySeat) return;
    const square = `${FILES[col]}${8 - row}`;
    const piece = board[row]![col];
    if (selected && targets.includes(square)) {
      // Bauernumwandlung -> automatisch Dame
      const fromPiece = pieceAt(board, selected);
      const promo = fromPiece?.type === 'p' && (square[1] === '8' || square[1] === '1') ? 'q' : '';
      onMove(`${selected}${square}${promo}`);
      setSelected(null);
      return;
    }
    if (piece && piece.color === mySeat) setSelected(square);
    else setSelected(null);
  };

  return (
    <div className="chess">
      {board.map((rowArr, row) =>
        rowArr.map((piece, col) => {
          const square = `${FILES[col]}${8 - row}`;
          const dark = (row + col) % 2 === 1;
          const cls = [
            'chess__sq', dark ? 'chess__sq--dark' : 'chess__sq--light',
            selected === square ? 'chess__sq--sel' : '',
            targets.includes(square) ? 'chess__sq--target' : '',
          ].join(' ');
          return (
            <button key={square} className={cls} onClick={() => clickSquare(row, col)}>
              {piece ? CHESS_GLYPHS[`${piece.color}${piece.type}`] : ''}
            </button>
          );
        }),
      )}
    </div>
  );
}

function pieceAt(board: ReturnType<typeof chessBoard>, square: string): { type: string; color: GamePlayer } | null {
  const col = FILES.indexOf(square[0]!);
  const row = 8 - Number(square[1]);
  return board[row]?.[col] ?? null;
}

function Connect4({ snap, myTurn, onMove }: { snap: GameSnapshot; myTurn: boolean; onMove: (m: string) => void }) {
  const cells = snap.board.split('');
  return (
    <div className="c4">
      {Array.from({ length: 7 }, (_, col) => (
        <button
          key={col}
          className="c4__col"
          disabled={!myTurn || cells[col] !== '.'}
          onClick={() => onMove(String(col))}
          title={`Spalte ${col + 1}`}
        >
          {Array.from({ length: 6 }, (_, row) => {
            const v = cells[row * 7 + col];
            return <span key={row} className={`c4__cell c4__cell--${v === 'w' ? 'red' : v === 'b' ? 'yellow' : 'empty'}`} />;
          })}
        </button>
      ))}
    </div>
  );
}

function TicTacToe({ snap, myTurn, onMove }: { snap: GameSnapshot; myTurn: boolean; onMove: (m: string) => void }) {
  const cells = snap.board.split('');
  return (
    <div className="ttt">
      {cells.map((v, i) => (
        <button
          key={i}
          className="ttt__cell"
          disabled={!myTurn || v !== '.'}
          onClick={() => onMove(String(i))}
        >
          {v === 'w' ? '✕' : v === 'b' ? '◯' : ''}
        </button>
      ))}
    </div>
  );
}
